import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { LogIn, AlertTriangle, Clock, ShieldAlert } from "lucide-react";
import LSSDLogo from "../components/LSSDLogo";
import GoogleSignInButton from "../components/GoogleSignInButton";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { consumePostLoginRedirect } from "../lib/postLoginRedirect";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const pending = params.get("pending") === "1";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      // Marca la sessione come "già entrata" così MDTBoot in DashboardLayout non
      // riparte per 5s e l'utente non vede una schermata verde bloccante.
      try { sessionStorage.setItem("lssd_boot_seen", "1"); } catch (err) { console.debug("[login] sessionStorage set fail", err); }
      nav(consumePostLoginRedirect(), { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Errore di autenticazione");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-[calc(100vh-120px)] bg-[var(--lssd-olive-deep)] flex items-center justify-center px-4 py-16" data-testid="login-page">
        <div className="w-full max-w-md">
          <div className="rounded-3xl bg-[var(--lssd-olive-dark)] border border-[var(--lssd-khaki)]/25 p-8 sm:p-10">
            <div className="flex flex-col items-center text-center mb-8">
              <LSSDLogo className="h-16 w-16 mb-4" />
              <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[var(--lssd-khaki)]">
                Portale Operatori
              </div>
              <h1 className="font-display font-black text-3xl tracking-tight text-[var(--lssd-cream)] mt-2">
                Accesso Sicuro
              </h1>
              <p className="text-[var(--lssd-cream)]/60 text-sm mt-2">
                Inserisci le tue credenziali ufficiali LSSD.
              </p>
            </div>

            {pending && (
              <div className="flex items-start gap-2 p-3 mb-5 bg-[var(--lssd-khaki)]/10 border border-[var(--lssd-khaki)]/40 rounded-2xl text-sm text-[var(--lssd-cream)]" data-testid="login-pending-banner">
                <Clock className="h-4 w-4 mt-0.5 shrink-0 text-[var(--lssd-khaki)]" />
                <span>Il tuo account è ancora in attesa di approvazione dal Comando.</span>
              </div>
            )}

            <div
              className="flex items-start gap-2 p-3 mb-5 bg-[var(--lssd-khaki)]/10 border border-[var(--lssd-khaki)]/35 rounded-2xl text-[12px] text-[var(--lssd-cream)] leading-relaxed"
              data-testid="login-real-data-warning"
            >
              <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0 text-[var(--lssd-khaki)]" />
              <span>
                <span className="font-semibold text-[var(--lssd-khaki)]">Attenzione:</span> inserisci
                sempre i tuoi <span className="font-semibold">dati reali</span>. Le credenziali
                fittizie non sono ammesse.
              </span>
            </div>

            <form onSubmit={submit} className="space-y-4" data-testid="login-form">
              <div className="mb-2">
                <GoogleSignInButton label="Accedi con Google" testId="login-google-btn" />
                <div className="mt-3 flex items-center gap-3">
                  <div className="flex-1 h-px bg-[var(--lssd-khaki)]/20" />
                  <span className="uppercase tracking-[0.2em] text-[10px] text-[var(--lssd-khaki)]/70">
                    oppure email
                  </span>
                  <div className="flex-1 h-px bg-[var(--lssd-khaki)]/20" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold text-[var(--lssd-cream)]/85 mb-1.5 block">Email</Label>
                <Input
                  data-testid="login-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="andrearossi@gmail.com"
                  autoComplete="email"
                  className="bg-[var(--lssd-olive)] border-[var(--lssd-khaki)]/30 text-[var(--lssd-cream)] placeholder:text-[var(--lssd-cream)]/40 rounded-2xl h-11"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-[var(--lssd-cream)]/85 mb-1.5 block">Password</Label>
                <Input
                  data-testid="login-password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  className="bg-[var(--lssd-olive)] border-[var(--lssd-khaki)]/30 text-[var(--lssd-cream)] placeholder:text-[var(--lssd-cream)]/40 rounded-2xl h-11"
                />
              </div>

              {error && (
                <div data-testid="login-error" className="flex items-start gap-2 p-3 bg-red-500/15 border border-red-500/40 rounded-2xl text-sm text-red-200">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                data-testid="login-submit"
                className="w-full bg-[var(--lssd-khaki)] hover:bg-[var(--lssd-khaki-light)] text-[var(--lssd-olive-deep)] rounded-full font-semibold uppercase tracking-[0.16em] h-11 transition-colors"
              >
                {loading ? "Accesso..." : (<><LogIn className="h-4 w-4 mr-2" /> Accedi</>)}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-[var(--lssd-khaki)]/15 text-center text-sm space-y-2">
              <div>
                <Link
                  to="/forgot-password"
                  className="text-[var(--lssd-khaki)] hover:text-[var(--lssd-cream)] transition-colors underline underline-offset-2"
                  data-testid="login-forgot-password-link"
                >
                  Password dimenticata?
                </Link>
              </div>
              <div>
                <span className="text-[var(--lssd-cream)]/55">Non hai un account?</span>{" "}
                <Link to="/register" className="text-[var(--lssd-khaki)] font-semibold hover:text-[var(--lssd-cream)] transition-colors" data-testid="login-to-register">
                  Richiedi accesso
                </Link>
              </div>
            </div>
          </div>
          <p className="text-center text-[10px] uppercase tracking-[0.24em] text-[var(--lssd-khaki)]/60 mt-6">
            A Tradition of Service Since 1852
          </p>
        </div>
      </div>
    </>
  );
}
